// ============================================================================
// AI PROPOSAL ASSISTANT
// Chat-based interface for proposal creation and management
// Extends shared AIAssistantChat with proposal-specific context
// Supports both site-based and standalone proposal workflows
// ============================================================================

'use client';

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Calculator,
  AlertTriangle,
  ExternalLink,
  FileText,
  FileCheck,
  History,
  Image as ImageIcon,
  Plus,
  Sparkles,
  Send,
  DollarSign,
  User,
  X,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { useAIAssistant } from '@/contexts/AIAssistantContext';
import { useAiProposalManagerSettings } from '@/hooks/useAiProposalManagerSettings';
import { useModuleColor } from '@/hooks/useModuleColor';
import { authService } from '@/lib/authService';
import { notify } from '@/lib/notifications';
import ProposalLineItemEditor from '@/components/proposals/ProposalLineItemEditor';
import { extractProposalReferenceDocuments } from '@/lib/proposals/proposalReferenceDocuments';
import AIAssistantChat, {
  type AIMessage,
  type AIAttachment,
  type AISuggestedAction,
  type QuickAction,
  type ExamplePrompt,
} from '@/components/shared/AIAssistantChat';
import ProposalSendDialog from './ProposalSendDialog';
import { dispatchOpenProposalWorkspace, openProposalClientView } from '@/lib/proposals/workItemWorkspace';
import type {
  Proposal,
  ProposalAttachment,
  ProposalAssistantContext,
  ProposalLineItem,
  ProposalRevision,
  ProposalSuggestedAction,
  ProposalSourceReference,
  CreateProposalRequest,
  ProposalConversationMessage,
} from '@/lib/proposals/proposalTypes';
import {
  PROPOSAL_EXAMPLE_PROMPTS,
  PROPOSAL_QUICK_ACTIONS,
  getProposalStatusLabel,
  getProposalStatusColor,
} from '@/lib/proposals/proposalConfig';
import { createLogger } from '@/lib/debugLogger';

const logger = createLogger('AIProposalAssistant');

const EDITABLE_PROPOSAL_STATUSES = new Set<Proposal['status']>(['draft', 'review', 'rejected']);

// ============================================================================
// TYPES & INTERFACES
// ============================================================================

interface AIProposalAssistantProps {
  proposal?: Proposal | null;
  proposalId?: string | null;
  sourceType?: 'site_based' | 'standalone';
  sourceReferences?: ProposalSourceReference[];
  contextWorkItemId?: string | null;
  contactId?: number | string | null;
  embedded?: boolean;
  compact?: boolean;
  includeContextMessage?: boolean;
  isFullWorkspace?: boolean;
  onClose?: () => void;
  onProposalCreated?: (proposal: Proposal) => void;
  onProposalUpdated?: (proposal: Proposal) => void;
  onConvertToDocument?: (proposal: Proposal, targetType: 'quote' | 'invoice') => void;
  onOpenConvertedDocument?: (proposal: Proposal) => void;
  onSendToClient?: (proposal: Proposal) => void;
  initialPrompt?: string;
  showProviderToggle?: boolean;
}

interface ProposalAssistantFileAttachment {
  name: string;
  type: string;
  size: number;
  content_base64: string;
}

interface ProposalReferenceAttachmentMetadata {
  file_name?: string;
  mime_type?: string | null;
  size_bytes?: number;
  uploaded_via?: string;
  extracted_from?: string;
  warnings?: string[];
  parsed_for_context?: boolean;
  text_preview?: string;
}

function normalizeProposalProvider(provider?: string): 'total-vision' | 'google' | 'openai' {
  if (provider === 'openai') {
    return 'openai';
  }

  if (provider === 'google' || provider === 'gemini') {
    return 'google';
  }

  // Default to self-hosted total-vision (zero cost)
  return 'total-vision';
}

function formatAttachmentFileSize(bytes?: number) {
  if (!bytes || bytes < 0) return null;
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

type ProposalSendMethod = 'email' | 'sms' | 'whatsapp' | 'share_link';

const PROPOSAL_NUMERIC_ID_FIELDS = ['contact_id', 'contact_location_id', 'contact_person_id'] as const;

function normalizeIntegerId(value: number | string | null | undefined): number | null {
  if (typeof value === 'number') {
    return Number.isInteger(value) && value > 0 ? value : null;
  }

  if (typeof value === 'string') {
    const trimmedValue = value.trim();
    if (!trimmedValue) {
      return null;
    }

    const parsedValue = Number(trimmedValue);
    return Number.isInteger(parsedValue) && parsedValue > 0 ? parsedValue : null;
  }

  return null;
}

function normalizeProposalPayloadIds<T extends Record<string, unknown>>(payload: T): T {
  const normalizedPayload: Record<string, unknown> = { ...payload };

  PROPOSAL_NUMERIC_ID_FIELDS.forEach((field) => {
    if (!(field in normalizedPayload)) {
      return;
    }

    const rawValue = normalizedPayload[field];
    if (rawValue == null) {
      normalizedPayload[field] = null;
      return;
    }

    const normalizedValue = normalizeIntegerId(rawValue as number | string | null | undefined);
    if (normalizedValue !== null) {
      normalizedPayload[field] = normalizedValue;
      return;
    }

    delete normalizedPayload[field];
  });

  return normalizedPayload as T;
}

// ============================================================================
// MAIN AI PROPOSAL ASSISTANT COMPONENT
// ============================================================================

export default function AIProposalAssistant({
  proposal,
  proposalId,
  sourceType = 'standalone',
  sourceReferences = [],
  contextWorkItemId,
  contactId,
  embedded = false,
  compact = false,
  includeContextMessage = true,
  isFullWorkspace = false,
  onClose,
  onProposalCreated,
  onProposalUpdated,
  onConvertToDocument,
  onOpenConvertedDocument,
  onSendToClient,
  initialPrompt,
  showProviderToggle = false,
}: AIProposalAssistantProps) {
  const { user } = useAuth();
  const {
    enabledConversionTargets,
    enabledDeliveryChannels,
    hasAnyDeliveryChannelEnabled,
    referenceExportsEnabled,
    referenceParsingEnabled,
    referenceUploadsEnabled,
  } = useAiProposalManagerSettings();
  const moduleColor = useModuleColor('total-operations');
  const {
    isGenerating,
    setIsGenerating,
    credits,
    refreshCredits,
    feedback,
    semanticContext,
    playbookContext,
  } = useAIAssistant({
    moduleKey: 'total-operations',
    moduleContext: 'proposals',
    enableSemanticContext: true,
    enableModuleAssistance: false,
  });
  const normalizedContactId = useMemo(() => normalizeIntegerId(contactId), [contactId]);
  const [loading, setLoading] = useState(false);
  const [loadingProposal, setLoadingProposal] = useState(false);
  const [currentProposal, setCurrentProposal] = useState<Proposal | null>(proposal || null);
  const [conversation, setConversation] = useState<AIMessage[]>([]);
  const [showLineItemEditor, setShowLineItemEditor] = useState(false);
  const [showRevisionHistory, setShowRevisionHistory] = useState(false);
  const [showSendDialog, setShowSendDialog] = useState(false);
  const [isSavingRevision, setIsSavingRevision] = useState(false);
  const [restoringRevisionId, setRestoringRevisionId] = useState<string | null>(null);
  const [statusActionLoading, setStatusActionLoading] = useState<'approved' | 'rejected' | null>(null);
  const initialPromptHandledRef = useRef(false);
  const isMountedRef = useRef(true);
  const currentProposalRef = useRef<Proposal | null>(proposal || null);
  const pendingPatchRef = useRef<Record<string, unknown>>({});
  const patchTimerRef = useRef<number | null>(null);
  const quickActionIcons = useMemo(() => ({
    FileText,
    Plus,
    History,
    Calculator,
    Send,
    FileCheck,
  }), []);

  const mapProposalAttachmentsToAIAttachments = useCallback((attachments?: ProposalAttachment[] | null): AIAttachment[] | undefined => {
    if (!attachments || attachments.length === 0) {
      return undefined;
    }

    return attachments.map((attachment) => ({
      type: attachment.type,
      title: attachment.title || attachment.file_name || 'Attachment',
      data: {
        id: attachment.id,
        url: attachment.url,
        file_name: attachment.file_name,
        reference_id: attachment.reference_id,
        metadata: attachment.metadata,
      },
    }));
  }, []);

  const mapAIAttachmentsToProposalAttachments = useCallback((attachments?: AIAttachment[]): ProposalAttachment[] | undefined => {
    if (!attachments || attachments.length === 0) {
      return undefined;
    }

    return attachments.map((attachment, index) => {
      const data = attachment.data && typeof attachment.data === 'object' && !Array.isArray(attachment.data)
        ? (attachment.data as Record<string, unknown>)
        : {};

      return {
        id: typeof data.id === 'string' ? data.id : `attachment-${index + 1}`,
        type: attachment.type as ProposalAttachment['type'],
        title: attachment.title,
        file_name: typeof data.file_name === 'string'
          ? data.file_name
          : typeof attachment.title === 'string' && attachment.title.trim().length > 0
            ? attachment.title
            : null,
        url: typeof data.url === 'string' ? data.url : null,
        reference_id: typeof data.reference_id === 'string' ? data.reference_id : null,
        metadata: data.metadata && typeof data.metadata === 'object' && !Array.isArray(data.metadata)
          ? (data.metadata as Record<string, unknown>)
          : undefined,
      };
    });
  }, []);

  const mapProposalActionsToAIActions = useCallback((actions?: ProposalSuggestedAction[] | null): AISuggestedAction[] | undefined => {
    if (!actions || actions.length === 0) {
      return undefined;
    }

    return actions.map((action) => ({
      id: action.id,
      label: action.label,
      type: action.type,
      data: action.payload,
    }));
  }, []);

  const mapAIActionsToProposalActions = useCallback((actions?: AISuggestedAction[]): ProposalSuggestedAction[] | undefined => {
    if (!actions || actions.length === 0) {
      return undefined;
    }

    return actions.map((action) => ({
      id: action.id,
      label: action.label,
      type: action.type as ProposalSuggestedAction['type'],
      payload: action.data && typeof action.data === 'object' && !Array.isArray(action.data)
        ? (action.data as Record<string, unknown>)
        : undefined,
    }));
  }, []);

  const buildDisplayAttachmentsFromFiles = useCallback((files?: globalThis.File[]): AIAttachment[] | undefined => {
    if (!files || files.length === 0) {
      return undefined;
    }

    return files.map((file, index) => ({
      type: file.type.startsWith('image/') ? 'image' : 'document',
      title: file.name,
      data: {
        id: `reference-file-${Date.now()}-${index}`,
        file_name: file.name,
        metadata: {
          file_name: file.name,
          mime_type: file.type || null,
          size_bytes: file.size,
          uploaded_via: 'proposal_reference_document',
        } satisfies ProposalReferenceAttachmentMetadata,
      },
    }));
  }, []);

  const encodeFilesForRequest = useCallback(async (files?: globalThis.File[]): Promise<ProposalAssistantFileAttachment[] | undefined> => {
    if (!files || files.length === 0) {
      return undefined;
    }

    return Promise.all(
      files.map((file) => new Promise<ProposalAssistantFileAttachment>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
          const base64 = (reader.result as string).split(',')[1] || '';
          resolve({
            name: file.name,
            type: file.type,
            size: file.size,
            content_base64: base64,
          });
        };
        reader.onerror = reject;
        reader.readAsDataURL(file);
      })),
    );
  }, []);

  const mapReferenceDocumentsToAIAttachments = useCallback((referenceDocuments?: Array<Record<string, unknown>>): AIAttachment[] | undefined => {
    if (!Array.isArray(referenceDocuments) || referenceDocuments.length === 0) {
      return undefined;
    }

    return referenceDocuments
      .filter((document): document is Record<string, unknown> => Boolean(document) && typeof document === 'object' && !Array.isArray(document))
      .map((document, index) => {
        const extractedFrom = typeof document.extracted_from === 'string' ? document.extracted_from : 'unsupported';
        const warnings = Array.isArray(document.warnings)
          ? document.warnings.filter((warning): warning is string => typeof warning === 'string' && warning.trim().length > 0)
          : [];

        return {
          type: extractedFrom === 'image' ? 'image' : 'document',
          title: typeof document.name === 'string' && document.name.trim().length > 0
            ? document.name
            : `Reference document ${index + 1}`,
          data: {
            id: `reference-doc-${index + 1}`,
            file_name: typeof document.name === 'string' ? document.name : undefined,
            metadata: {
              file_name: typeof document.name === 'string' ? document.name : undefined,
              mime_type: typeof document.type === 'string' ? document.type : null,
              size_bytes: typeof document.size === 'number' ? document.size : undefined,
              extracted_from: extractedFrom,
              warnings,
              parsed_for_context: true,
              text_preview: typeof document.text_preview === 'string' ? document.text_preview : undefined,
            } satisfies ProposalReferenceAttachmentMetadata,
          },
        };
      });
  }, []);

  const renderProposalAttachment = useCallback((attachment: AIAttachment) => {
    const data = attachment.data && typeof attachment.data === 'object' && !Array.isArray(attachment.data)
      ? (attachment.data as Record<string, unknown>)
      : {};
    const metadata = data.metadata && typeof data.metadata === 'object' && !Array.isArray(data.metadata)
      ? (data.metadata as ProposalReferenceAttachmentMetadata)
      : undefined;

    const title = attachment.title
      || (typeof data.file_name === 'string' ? data.file_name : null)
      || metadata?.file_name
      || 'Reference document';
    const extractedFrom = metadata?.extracted_from;
    const warnings = Array.isArray(metadata?.warnings) ? metadata?.warnings.filter(Boolean) : [];
    const previewText = typeof metadata?.text_preview === 'string' && metadata.text_preview.trim().length > 0
      ? metadata.text_preview
      : null;
    const fileSizeLabel = formatAttachmentFileSize(metadata?.size_bytes);
    const isParsedReference = metadata?.parsed_for_context === true;
    const Icon = attachment.type === 'image' ? ImageIcon : FileText;

    return (
      <div className="rounded-xl border border-gray-200 bg-white px-3 py-3 shadow-sm dark:border-gray-700 dark:bg-gray-900/70">
        <div className="flex items-start gap-3">
          <div className={`mt-0.5 flex h-9 w-9 items-center justify-center rounded-lg ${attachment.type === 'image' ? 'bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-300' : 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300'}`}>
            <Icon className="h-4 w-4" />
          </div>
          <div className="min-w-0 flex-1 space-y-1">
            <div className="flex flex-wrap items-center gap-2">
              <p className="truncate text-sm font-medium text-gray-900 dark:text-gray-100">{title}</p>
              {isParsedReference && (
                <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-medium text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300">
                  parsed for context
                </span>
              )}
            </div>
            <div className="flex flex-wrap items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
              {extractedFrom && <span>Source: {extractedFrom.toUpperCase()}</span>}
              {fileSizeLabel && <span>{fileSizeLabel}</span>}
            </div>
            {warnings.length > 0 && (
              <div className="flex items-start gap-1.5 rounded-lg bg-amber-50 px-2 py-1.5 text-xs text-amber-700 dark:bg-amber-900/20 dark:text-amber-300">
                <AlertTriangle className="mt-0.5 h-3.5 w-3.5 flex-shrink-0" />
                <span>{warnings[0]}</span>
              </div>
            )}
            {previewText && (
              <p className="line-clamp-3 text-xs text-gray-600 dark:text-gray-300">{previewText}</p>
            )}
          </div>
        </div>
      </div>
    );
  }, []);

  const mapProposalConversationToChatMessages = useCallback((nextProposal: Proposal | null): AIMessage[] => {
    if (!nextProposal) {
      return [];
    }

    const messages: AIMessage[] = includeContextMessage
      ? [
        {
          id: 'proposal-context',
          role: 'system',
          content: `Current Quote Context:
- Title: ${nextProposal.title}
- Status: ${getProposalStatusLabel(nextProposal.status)}
- Source: ${nextProposal.source_type}
- Line Items: ${nextProposal.line_items?.length || 0}
- Total: ${nextProposal.currency} ${typeof nextProposal.grand_total === 'number' ? nextProposal.grand_total.toFixed(2) : 'N/A'}
${nextProposal.description ? `- Description: ${nextProposal.description}` : ''}
${nextProposal.source_references?.length ? `- Source Jobs: ${nextProposal.source_references.map((job) => job.work_item_number || job.title).join(', ')}` : ''}`,
          timestamp: new Date().toISOString(),
        },
      ]
      : [];

    if (nextProposal.conversation?.length > 0) {
      messages.push(
        ...nextProposal.conversation.map((message) => ({
          id: message.id,
          role: message.role,
          content: message.content,
          timestamp: message.timestamp,
          attachments: mapProposalAttachmentsToAIAttachments(message.attachments),
          actions: mapProposalActionsToAIActions(message.suggested_actions),
        })),
      );
    }

    return messages;
  }, [includeContextMessage, mapProposalActionsToAIActions, mapProposalAttachmentsToAIAttachments]);

  const serializeConversation = useCallback((messages: AIMessage[]): ProposalConversationMessage[] => {
    return messages
      .filter((message) => message.role === 'user' || message.role === 'assistant' || message.role === 'system')
      .filter((message) => message.id !== 'proposal-context')
      .map((message) => ({
        id: message.id,
        role: message.role,
        content: message.content,
        timestamp: message.timestamp,
        attachments: mapAIAttachmentsToProposalAttachments(message.attachments),
        suggested_actions: mapAIActionsToProposalActions(message.actions),
      }));
  }, [mapAIActionsToProposalActions, mapAIAttachmentsToProposalAttachments]);

  const applyLoadedProposal = useCallback((nextProposal: Proposal, reason: 'created' | 'updated' | 'loaded' = 'updated') => {
    currentProposalRef.current = nextProposal;
    setCurrentProposal(nextProposal);
    setConversation(mapProposalConversationToChatMessages(nextProposal));

    if (reason === 'created') {
      onProposalCreated?.(nextProposal);
      return;
    }

    if (reason === 'updated') {
      onProposalUpdated?.(nextProposal);
    }
  }, [mapProposalConversationToChatMessages, onProposalCreated, onProposalUpdated]);

  const patchProposalRequest = useCallback(async (proposalToPatchId: string, patch: Record<string, unknown>) => {
    const token = authService.getTokens()?.access_token;
    if (!token) {
      throw new Error('Not authenticated');
    }

    const normalizedPatch = normalizeProposalPayloadIds(patch);

    const response = await fetch(`/api/v1/proposals/${proposalToPatchId}`, {
      method: 'PATCH',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(normalizedPatch),
    });

    if (!response.ok) {
      const errorPayload = await response.json().catch(() => null);
      throw new Error(errorPayload?.error || 'Failed to update quote');
    }

    const data = await response.json();
    return data.data as Proposal;
  }, []);

  const persistProposalPatch = useCallback(async (
    proposalToPatchId: string,
    patch: Record<string, unknown>,
    options?: { applyUpdate?: boolean },
  ) => {
    const updatedProposal = await patchProposalRequest(proposalToPatchId, patch);

    if (options?.applyUpdate !== false && isMountedRef.current) {
      applyLoadedProposal(updatedProposal, 'updated');
    }

    return updatedProposal;
  }, [applyLoadedProposal, patchProposalRequest]);

  const flushPendingProposalPatch = useCallback(async (options?: { applyUpdate?: boolean; notifyOnError?: boolean }) => {
    if (patchTimerRef.current !== null) {
      window.clearTimeout(patchTimerRef.current);
      patchTimerRef.current = null;
    }

    const proposalToPatchId = currentProposalRef.current?.id;
    const payload = pendingPatchRef.current;
    pendingPatchRef.current = {};

    if (!proposalToPatchId || Object.keys(payload).length === 0) {
      return currentProposalRef.current;
    }

    try {
      return await persistProposalPatch(proposalToPatchId, payload, {
        applyUpdate: options?.applyUpdate,
      });
    } catch (error) {
      pendingPatchRef.current = {
        ...payload,
        ...pendingPatchRef.current,
      };

      logger.error('[AIProposalAssistant] Error saving proposal changes:', error);
      if (options?.notifyOnError !== false) {
        notify.error('Failed to save proposal changes');
      }
      throw error;
    }
  }, [persistProposalPatch]);

  const queueProposalPatch = useCallback((patch: Record<string, unknown>) => {
    if (!currentProposalRef.current?.id) {
      return;
    }

    pendingPatchRef.current = {
      ...pendingPatchRef.current,
      ...patch,
    };

    if (patchTimerRef.current !== null) {
      window.clearTimeout(patchTimerRef.current);
    }

    patchTimerRef.current = window.setTimeout(async () => {
      try {
        patchTimerRef.current = null;
        await flushPendingProposalPatch();
      } catch (error) {
        logger.error('[AIProposalAssistant] Error saving queued proposal changes:', error);
      }
    }, 700);
  }, [flushPendingProposalPatch]);

  const buildAssistantContext = useCallback((activeProposal?: Proposal | null): ProposalAssistantContext => ({
    proposal_id: activeProposal?.id || proposalId || '',
    proposal_number: activeProposal?.proposal_number,
    title: activeProposal?.title || 'New Quote',
    status: activeProposal?.status || 'draft',
    source_type: activeProposal?.source_type || sourceType,
    parent_work_item_id: activeProposal?.parent_work_item_id || contextWorkItemId || undefined,
    contact_id: normalizeIntegerId(activeProposal?.contact_id) ?? normalizedContactId,
    contact_location_id: normalizeIntegerId(activeProposal?.contact_location_id),
    contact_name: activeProposal?.contact_name,
    line_items_count: activeProposal?.line_items?.length || 0,
    grand_total: activeProposal?.grand_total || 0,
    currency: activeProposal?.currency || 'ZAR',
    available_jobs: activeProposal?.source_references || sourceReferences,
    similar_proposals: activeProposal?.similar_proposals,
  }), [contextWorkItemId, normalizedContactId, proposalId, sourceReferences, sourceType]);

  const extractPersistableProposalUpdates = useCallback((proposalUpdates: unknown) => {
    if (!proposalUpdates || typeof proposalUpdates !== 'object' || Array.isArray(proposalUpdates)) {
      return {};
    }

    const updates = proposalUpdates as Record<string, unknown>;
    const patch: Record<string, unknown> = {};

    const allowedFields = [
      'title',
      'description',
      'contact_id',
      'contact_location_id',
      'contact_person_id',
      'contact_person_name',
      'line_items',
      'source_references',
      'similar_proposals',
      'discount_percentage',
      'valid_until',
      'notes',
      'internal_notes',
      'status',
    ];

    for (const field of allowedFields) {
      if (field in updates) {
        if (field === 'contact_id' || field === 'contact_location_id' || field === 'contact_person_id') {
          const rawValue = updates[field];
          if (rawValue == null) {
            patch[field] = null;
            continue;
          }

          const normalizedValue = normalizeIntegerId(rawValue as number | string | null | undefined);
          if (normalizedValue !== null) {
            patch[field] = normalizedValue;
          }
          continue;
        }

        patch[field] = updates[field];
      }
    }

    return patch;
  }, []);

  // Quick actions for proposal workflow
  const quickActions = useMemo<QuickAction[]>(() => {
    return PROPOSAL_QUICK_ACTIONS
      .filter((action) => action.source_type === 'any' || action.source_type === sourceType)
      .filter((action) => {
        if (!currentProposal) {
          return true;
        }

        switch (action.id) {
          case 'generate_from_site':
          case 'create_standalone':
            return false;
          case 'send_to_client':
            return hasAnyDeliveryChannelEnabled && (currentProposal.status === 'draft' || currentProposal.status === 'review' || currentProposal.status === 'rejected');
          case 'convert_to_quote':
            return enabledConversionTargets.includes('quote') && currentProposal.status === 'approved';
          default:
            return true;
        }
      })
      .map((action) => ({
        id: action.id,
        label: action.label,
        description: action.description,
        icon: quickActionIcons[action.icon as keyof typeof quickActionIcons] || Sparkles,
      }));
  }, [currentProposal, enabledConversionTargets, hasAnyDeliveryChannelEnabled, quickActionIcons, sourceType]);

  const lifecycleState = useMemo(() => {
    if (!currentProposal) {
      return null;
    }

    switch (currentProposal.status) {
      case 'draft':
        return {
          tone: 'border-gray-200 bg-white/70 dark:border-gray-700 dark:bg-gray-900/40',
          title: 'Still shaping the proposal',
          description: 'Refine the scope, pricing, and wording here. When it is ready for external review, send it to the client for approval.',
        };
      case 'review':
        return {
          tone: 'border-blue-200 bg-blue-50 dark:border-blue-900/40 dark:bg-blue-950/30',
          title: 'Ready for internal review',
          description: 'This proposal looks close. Confirm the commercial details, then send it to the client for approval.',
        };
      case 'pending_approval':
        return {
          tone: 'border-amber-200 bg-amber-50 dark:border-amber-900/40 dark:bg-amber-950/30',
          title: 'Awaiting client approval',
          description: currentProposal.sent_to_client_at
            ? `Sent to the client on ${new Date(currentProposal.sent_to_client_at).toLocaleDateString()}. Once approved, convert it into a finance document here.`
            : 'This quote has been sent to the client. Once approved, convert it into a finance document here.',
        };
      case 'approved':
        return {
          tone: 'border-green-200 bg-green-50 dark:border-green-900/40 dark:bg-green-950/30',
          title: 'Approved and ready for finance',
          description: 'The client has approved this proposal. Convert it to a quote or invoice to move it into the finance lifecycle.',
        };
      case 'converted':
        return {
          tone: 'border-purple-200 bg-purple-50 dark:border-purple-900/40 dark:bg-purple-950/30',
          title: 'Converted to finance document',
          description: `This proposal has already been converted to a ${currentProposal.converted_document_type || 'finance document'}. Open it to continue the lifecycle in Finance.`,
        };
      case 'rejected':
        return {
          tone: 'border-red-200 bg-red-50 dark:border-red-900/40 dark:bg-red-950/30',
          title: 'Rejected by the client',
          description: 'Update the proposal based on feedback or prepare a revised version before sending it again.',
        };
      case 'cancelled':
        return {
          tone: 'border-gray-300 bg-gray-100 dark:border-gray-700 dark:bg-gray-900/60',
          title: 'Quote cancelled',
          description: 'This proposal is no longer active. Start a fresh revision if the opportunity reopens.',
        };
      default:
        return null;
    }
  }, [currentProposal]);

  const sortedRevisions = useMemo<ProposalRevision[]>(() => {
    return [...(currentProposal?.revisions || [])].sort((left, right) => right.revision_number - left.revision_number);
  }, [currentProposal?.revisions]);

  const referenceDocuments = useMemo(() => {
    return extractProposalReferenceDocuments(serializeConversation(conversation));
  }, [conversation, serializeConversation]);

  const isManualProposalEditingAllowed = useMemo(() => {
    if (!currentProposal) {
      return false;
    }

    return EDITABLE_PROPOSAL_STATUSES.has(currentProposal.status);
  }, [currentProposal]);

  const canOpenFullWorkspace = useMemo(() => {
    return Boolean(currentProposal?.id) && !isFullWorkspace;
  }, [currentProposal?.id, isFullWorkspace]);

  // Example prompts
  const examplePrompts = useMemo<ExamplePrompt[]>(() => {
    return PROPOSAL_EXAMPLE_PROMPTS.map((prompt) => ({
      text: prompt,
      icon: Sparkles,
    }));
  }, []);

  // Handle AI message sending
  const handleCreateProposal = useCallback(async (request: CreateProposalRequest) => {
    setLoading(true);

    try {
      const token = authService.getTokens()?.access_token;
      if (!token) {
        throw new Error('Not authenticated');
      }

      const normalizedRequest = normalizeProposalPayloadIds(request as CreateProposalRequest & Record<string, unknown>);

      const response = await fetch('/api/v1/proposals', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(normalizedRequest),
      });

      if (!response.ok) {
        throw new Error('Failed to create quote');
      }

      const data = await response.json();
      const newProposal = data.data as Proposal;
      applyLoadedProposal(newProposal, 'created');
      notify.success('Quote created successfully');

      return newProposal;
    } catch (error) {
      logger.error('[AIProposalAssistant] Error creating quote:', error);
      notify.error(error instanceof Error ? error.message : 'Failed to create quote');
      return null;
    } finally {
      setLoading(false);
    }
  }, [applyLoadedProposal]);

  const createRevision = useCallback(async () => {
    const proposalToRevise = await flushPendingProposalPatch().catch(() => null) || currentProposalRef.current;
    if (!proposalToRevise) {
      return;
    }

    setIsSavingRevision(true);
    try {
      const token = authService.getTokens()?.access_token;
      if (!token) {
        throw new Error('Not authenticated');
      }

      const response = await fetch('/api/v1/proposals', {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          action: 'create_revision',
          proposal_id: proposalToRevise.id,
          label: `Revision ${(proposalToRevise.current_revision_number || proposalToRevise.revisions?.length || 0) + 1}`,
        }),
      });

      const data = await response.json().catch(() => null);
      if (!response.ok || !data?.success) {
        throw new Error(data?.error || 'Failed to create quote revision');
      }

      applyLoadedProposal(data.data as Proposal, 'updated');
      setShowRevisionHistory(true);
      notify.success('Quote revision saved');
    } catch (error) {
      logger.error('[AIProposalAssistant] Error creating quote revision:', error);
      notify.error(error instanceof Error ? error.message : 'Failed to create quote revision');
    } finally {
      setIsSavingRevision(false);
    }
  }, [applyLoadedProposal, flushPendingProposalPatch]);

  const restoreRevision = useCallback(async (revisionId: string) => {
    const proposalToRestore = await flushPendingProposalPatch().catch(() => null) || currentProposalRef.current;
    if (!proposalToRestore) {
      return;
    }

    setRestoringRevisionId(revisionId);
    try {
      const token = authService.getTokens()?.access_token;
      if (!token) {
        throw new Error('Not authenticated');
      }

      const response = await fetch('/api/v1/proposals', {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          action: 'restore_revision',
          proposal_id: proposalToRestore.id,
          revision_id: revisionId,
        }),
      });

      const data = await response.json().catch(() => null);
      if (!response.ok || !data?.success) {
        throw new Error(data?.error || 'Failed to restore proposal revision');
      }

      applyLoadedProposal(data.data as Proposal, 'updated');
      notify.success('Quote revision restored');
    } catch (error) {
      logger.error('[AIProposalAssistant] Error restoring quote revision:', error);
      notify.error(error instanceof Error ? error.message : 'Failed to restore quote revision');
    } finally {
      setRestoringRevisionId(null);
    }
  }, [applyLoadedProposal, flushPendingProposalPatch]);

  const handleOpenSendDialog = useCallback(async () => {
    try {
      await flushPendingProposalPatch();
      setShowSendDialog(true);
    } catch {
      // Error notification handled by flushPendingProposalPatch.
    }
  }, [flushPendingProposalPatch]);

  const handleUpdateProposalStatus = useCallback(async (nextStatus: 'approved' | 'rejected') => {
    const proposalToUpdate = await flushPendingProposalPatch().catch(() => null) || currentProposalRef.current;
    if (!proposalToUpdate?.id) {
      notify.info('Create or load a proposal first.');
      return;
    }

    setStatusActionLoading(nextStatus);
    try {
      const updatedProposal = await patchProposalRequest(proposalToUpdate.id, { status: nextStatus });
      applyLoadedProposal(updatedProposal, 'updated');
      notify.success(nextStatus === 'approved' ? 'Quote approved' : 'Quote rejected');
    } catch (error) {
      logger.error('[AIProposalAssistant] Error updating proposal status:', error);
      notify.error(error instanceof Error ? error.message : `Failed to mark quote as ${nextStatus}`);
    } finally {
      setStatusActionLoading(null);
    }
  }, [applyLoadedProposal, flushPendingProposalPatch, patchProposalRequest]);

  const handleConvertDocument = useCallback(async (targetType: 'quote' | 'invoice') => {
    try {
      const proposalToConvert = await flushPendingProposalPatch() || currentProposalRef.current;
      if (!proposalToConvert) {
        return;
      }

      onConvertToDocument?.(proposalToConvert, targetType);
    } catch {
      // Error notification handled by flushPendingProposalPatch.
    }
  }, [flushPendingProposalPatch, onConvertToDocument]);

  const handleOpenConvertedDocument = useCallback(async () => {
    try {
      const proposalToOpen = await flushPendingProposalPatch() || currentProposalRef.current;
      if (!proposalToOpen) {
        return;
      }

      if (onOpenConvertedDocument) {
        onOpenConvertedDocument(proposalToOpen);
      } else {
        notify.info('This proposal has already been converted to a finance document.');
      }
    } catch {
      // Error notification handled by flushPendingProposalPatch.
    }
  }, [flushPendingProposalPatch, onOpenConvertedDocument]);

  const handleOpenProposalWorkspace = useCallback(async () => {
    try {
      const proposalToOpen = await flushPendingProposalPatch() || currentProposalRef.current;
      if (!proposalToOpen?.id) {
        notify.info('Create or load a proposal first to open the full workspace.');
        return;
      }

      dispatchOpenProposalWorkspace({
        proposalId: proposalToOpen.id,
        initialTab: 'overview',
      });
    } catch {
      // Error notification handled by flushPendingProposalPatch.
    }
  }, [flushPendingProposalPatch]);

  const handleOpenClientPresentation = useCallback(async () => {
    try {
      const proposalToOpen = await flushPendingProposalPatch() || currentProposalRef.current;
      if (!proposalToOpen?.proposal_permalink_url || !openProposalClientView(proposalToOpen.proposal_permalink_url)) {
        notify.info('This proposal does not have a client presentation link yet.');
      }
    } catch {
      // Error notification handled by flushPendingProposalPatch.
    }
  }, [flushPendingProposalPatch]);

  const handleAssistantClose = useCallback(async () => {
    if (!onClose) {
      return;
    }

    try {
      await flushPendingProposalPatch({ applyUpdate: false });
      onClose();
    } catch {
      // Error notification handled by flushPendingProposalPatch.
    }
  }, [flushPendingProposalPatch, onClose]);

  const ensureActiveProposal = useCallback(async (initialMessage?: string) => {
    if (currentProposal) {
      return currentProposal;
    }

    return handleCreateProposal({
      source_type: sourceType,
      contact_id: normalizedContactId,
      parent_work_item_id: contextWorkItemId ?? null,
      initial_message: initialMessage,
      source_references: sourceReferences,
    });
  }, [contextWorkItemId, currentProposal, handleCreateProposal, normalizedContactId, sourceReferences, sourceType]);

  const handleSendMessage = useCallback(async (message: string, files?: globalThis.File[], options?: { provider?: string; model?: string }) => {
    const userAttachments = buildDisplayAttachmentsFromFiles(files);
    const userMessage: AIMessage = {
      id: `user-${Date.now()}`,
      role: 'user',
      content: message,
      timestamp: new Date().toISOString(),
      attachments: userAttachments,
    };
    const nextConversation = [...conversation, userMessage];
    setConversation(nextConversation);
    setLoading(true);
    setIsGenerating(true);

    try {
      const syncedProposal = currentProposalRef.current?.id
        ? await flushPendingProposalPatch().catch(() => null) || currentProposalRef.current
        : currentProposalRef.current;

      const activeProposal = syncedProposal || await ensureActiveProposal(message);
      if (!activeProposal) {
        throw new Error('Failed to initialize proposal');
      }

      const token = authService.getTokens()?.access_token;
      if (!token) {
        throw new Error('Not authenticated');
      }

      const attachments = await encodeFilesForRequest(files);

      const response = await fetch('/api/v1/ai-assistant', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          moduleContext: 'proposals',
          context: buildAssistantContext(activeProposal),
          semanticContext: semanticContext || undefined,
          playbookContext: playbookContext || undefined,
          message,
          provider: normalizeProposalProvider(options?.provider),
          model: options?.model || 'qwen3.5:4b',
          conversationHistory: nextConversation,
          ...(attachments && { attachments }),
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to get AI response');
      }

      const data = await response.json();

      const aiMessage: AIMessage = {
        id: `ai-${Date.now()}`,
        role: 'assistant',
        content: data.response || data.message || '',
        timestamp: new Date().toISOString(),
        attachments: mapReferenceDocumentsToAIAttachments(data.referenceDocuments),
        actions: Array.isArray(data.suggestedActions)
          ? data.suggestedActions
          : Array.isArray(data.actions)
            ? data.actions
            : undefined,
      };

      const updatedConversation = [...nextConversation, aiMessage];
      setConversation(updatedConversation);

      const patchPayload = {
        ...extractPersistableProposalUpdates(data.proposalUpdates),
        conversation: serializeConversation(updatedConversation),
      };

      if (Object.keys(patchPayload).length > 0) {
        await persistProposalPatch(activeProposal.id, patchPayload);
      }

      await refreshCredits();
    } catch (error) {
      logger.error('[AIProposalAssistant] Error sending message:', error);
      notify.error(error instanceof Error ? error.message : 'Failed to get AI response');
    } finally {
      setLoading(false);
      setIsGenerating(false);
    }
  }, [buildAssistantContext, buildDisplayAttachmentsFromFiles, conversation, encodeFilesForRequest, ensureActiveProposal, extractPersistableProposalUpdates, flushPendingProposalPatch, mapReferenceDocumentsToAIAttachments, persistProposalPatch, playbookContext, refreshCredits, semanticContext, serializeConversation, setIsGenerating]);

  // Handle suggested action execution
  const handleExecuteAction = useCallback(async (action: AISuggestedAction) => {
    if (!currentProposal) return;

    const actionData = action.data && typeof action.data === 'object' && !Array.isArray(action.data)
      ? (action.data as Record<string, unknown>)
      : {};

    switch (action.type) {
      case 'generate_document':
        if (currentProposal.status === 'converted') {
          await handleOpenConvertedDocument();
          break;
        }

        if (currentProposal.status !== 'approved') {
          notify.info('Approve the proposal before converting it to a finance document.');
          break;
        }

        if (actionData.targetType === 'invoice' && !enabledConversionTargets.includes('invoice')) {
          notify.info('Invoice conversion is disabled for this client.');
          break;
        }

        if ((!actionData.targetType || actionData.targetType !== 'invoice') && !enabledConversionTargets.includes('quote')) {
          notify.info('Quote conversion is disabled for this client.');
          break;
        }

        await handleConvertDocument(actionData.targetType === 'invoice' ? 'invoice' : 'quote');
        break;

      case 'send_to_client':
        if (currentProposal.status === 'pending_approval') {
          notify.info('This proposal is already awaiting client approval.');
          break;
        }

        if (currentProposal.status === 'approved') {
          notify.info('This proposal is already approved and ready for finance conversion.');
          break;
        }

        if (currentProposal.status === 'converted') {
          await handleOpenConvertedDocument();
          break;
        }

        if (!hasAnyDeliveryChannelEnabled) {
          notify.info('No client-approved delivery channels are enabled for this proposal.');
          break;
        }

        await handleOpenSendDialog();
        break;

      case 'add_line_item':
        if (actionData.lineItem && typeof actionData.lineItem === 'object') {
          setCurrentProposal((prev) => {
            if (!prev) {
              return prev;
            }

            const nextLineItems = [...prev.line_items, actionData.lineItem as ProposalLineItem];
            queueProposalPatch({ line_items: nextLineItems });

            return {
              ...prev,
              line_items: nextLineItems,
            };
          });
        }
        break;

      case 'adjust_pricing':
        {
          const adjustments = Array.isArray(actionData.adjustments) ? actionData.adjustments : null;
          if (!adjustments) {
            break;
          }

          setCurrentProposal((prev) => {
            if (!prev) {
              return prev;
            }

            const updated = { ...prev! };
            adjustments.forEach((adjustment: unknown) => {
              if (
                adjustment
                && typeof adjustment === 'object'
                && 'field' in adjustment
                && 'value' in adjustment
                && adjustment.field === 'discount_percentage'
                && typeof adjustment.value === 'number'
              ) {
                updated.discount_percentage = adjustment.value;
              }
            });
            queueProposalPatch({ discount_percentage: updated.discount_percentage || 0 });
            return updated;
          });
        }
        break;

      default:
        // Send action as message to AI for handling
        await handleSendMessage(action.label);
    }
  }, [currentProposal, enabledConversionTargets, handleConvertDocument, handleOpenConvertedDocument, handleOpenSendDialog, handleSendMessage, hasAnyDeliveryChannelEnabled, queueProposalPatch]);

  useEffect(() => {
    currentProposalRef.current = currentProposal;
  }, [currentProposal]);

  useEffect(() => {
    if (proposal) {
      applyLoadedProposal(proposal, 'loaded');
      return;
    }

    if (!proposalId) {
      setCurrentProposal(null);
      setConversation([]);
    }
  }, [applyLoadedProposal, proposal, proposalId]);

  useEffect(() => {
    const loadProposal = async () => {
      if (proposal || !proposalId) {
        return;
      }

      setLoadingProposal(true);

      try {
        const token = authService.getTokens()?.access_token;
        if (!token) {
          throw new Error('Not authenticated');
        }

        const response = await fetch(`/api/v1/proposals/${proposalId}`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (!response.ok) {
          throw new Error('Failed to load proposal');
        }

        const data = await response.json();
        applyLoadedProposal(data.data as Proposal, 'loaded');
      } catch (error) {
        logger.error('[AIProposalAssistant] Error loading proposal:', error);
        notify.error('Failed to load proposal');
      } finally {
        setLoadingProposal(false);
      }
    };

    void loadProposal();
  }, [applyLoadedProposal, proposal, proposalId]);

  useEffect(() => {
    isMountedRef.current = true;

    return () => {
      isMountedRef.current = false;

      if (patchTimerRef.current !== null) {
        window.clearTimeout(patchTimerRef.current);
        patchTimerRef.current = null;
      }

      const proposalToPatchId = currentProposalRef.current?.id;
      const payload = pendingPatchRef.current;
      pendingPatchRef.current = {};

      if (proposalToPatchId && Object.keys(payload).length > 0) {
        void persistProposalPatch(proposalToPatchId, payload, { applyUpdate: false }).catch((error) => {
          logger.error('[AIProposalAssistant] Error flushing proposal changes on unmount:', error);
        });
      }
    };
  }, [persistProposalPatch]);

  // Handle initial prompt
  useEffect(() => {
    if (initialPrompt && !initialPromptHandledRef.current && !loadingProposal) {
      initialPromptHandledRef.current = true;
      void handleSendMessage(initialPrompt);
    }
  }, [handleSendMessage, initialPrompt, loadingProposal]);

  return (
    <div className="flex flex-col h-full">
      {currentProposal && (
        <ProposalSendDialog
          isOpen={showSendDialog}
          proposal={currentProposal}
          allowedSendMethods={enabledDeliveryChannels as ProposalSendMethod[]}
          initialSendMethod="email"
          onClose={() => setShowSendDialog(false)}
          onSent={(proposal, delivery) => {
            applyLoadedProposal(proposal, 'updated');
            onSendToClient?.(proposal);

            if (delivery?.channel !== 'share_link') {
              setShowSendDialog(false);
            }
          }}
        />
      )}

      {/* Proposal Summary */}
      {!compact && currentProposal && (
        <div className="px-4 py-3 bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <FileText className="w-4 h-4 text-gray-600 dark:text-gray-400" />
              <span className="font-medium text-gray-900 dark:text-gray-100">
                {currentProposal.title}
              </span>
              {currentProposal.proposal_number && (
                <span className="text-sm text-gray-600 dark:text-gray-400">
                  #{currentProposal.proposal_number}
                </span>
              )}
            </div>
            <span className={`px-2 py-1 text-xs font-medium rounded ${getProposalStatusColor(currentProposal.status)}`}>
              {getProposalStatusLabel(currentProposal.status)}
            </span>
          </div>
          <div className="flex items-center gap-4 text-sm text-gray-600 dark:text-gray-400">
            {typeof currentProposal.grand_total === 'number' && (
              <div className="flex items-center gap-1">
                <DollarSign className="w-3 h-3" />
                <span>{currentProposal.currency} {currentProposal.grand_total.toFixed(2)}</span>
              </div>
            )}
            <div className="flex items-center gap-1">
              <FileText className="w-3 h-3" />
              <span>{currentProposal.line_items?.length || 0} items</span>
            </div>
            {currentProposal.contact_name && (
              <div className="flex items-center gap-1">
                <User className="w-3 h-3" />
                <span>{currentProposal.contact_name}</span>
              </div>
            )}
          </div>

          {(canOpenFullWorkspace || currentProposal.proposal_permalink_url) && (
            <div className="mt-3 flex flex-wrap items-center gap-2">
              {canOpenFullWorkspace && (
                <button
                  onClick={() => void handleOpenProposalWorkspace()}
                  className="inline-flex items-center gap-1.5 rounded-lg border border-blue-200 bg-blue-600 px-3 py-2 text-xs font-medium text-white hover:bg-blue-700 dark:border-blue-900/40"
                >
                  <ExternalLink className="w-3.5 h-3.5" />
                  Open full workspace
                </button>
              )}
              {currentProposal.proposal_permalink_url && (
                <button
                  onClick={() => void handleOpenClientPresentation()}
                  className="inline-flex items-center gap-1.5 rounded-lg border border-blue-200 bg-blue-50 px-3 py-2 text-xs font-medium text-blue-700 hover:bg-blue-100 dark:border-blue-900/40 dark:bg-blue-950/30 dark:text-blue-300 dark:hover:bg-blue-950/50"
                >
                  <ExternalLink className="w-3.5 h-3.5" />
                  Open client presentation
                </button>
              )}
              <span className="text-xs text-gray-500 dark:text-gray-400">
                Jump into the enterprise quote workspace or open the share-ready client presentation.
              </span>
            </div>
          )}

          {lifecycleState && (
            <div className={`mt-3 rounded-xl border px-3 py-3 ${lifecycleState.tone}`}>
              <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                    {lifecycleState.title}
                  </p>
                  <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">
                    {lifecycleState.description}
                  </p>
                </div>

                <div className="flex flex-wrap items-center gap-2">
                  {(currentProposal.status === 'draft' || currentProposal.status === 'review' || currentProposal.status === 'rejected') && (
                    <button
                      onClick={() => void handleOpenSendDialog()}
                      disabled={!hasAnyDeliveryChannelEnabled}
                      className="inline-flex items-center gap-1.5 rounded-lg border border-blue-200 bg-blue-600 px-3 py-2 text-xs font-medium text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60 dark:border-blue-900/40"
                    >
                      <Send className="w-3.5 h-3.5" />
                      {currentProposal.status === 'rejected' ? 'Re-send to Client' : 'Send to Client'}
                    </button>
                  )}

                  {currentProposal.status === 'pending_approval' && (
                    <>
                      <button
                        onClick={() => void handleUpdateProposalStatus('approved')}
                        disabled={statusActionLoading !== null}
                        className="inline-flex items-center gap-1.5 rounded-lg border border-green-200 bg-green-600 px-3 py-2 text-xs font-medium text-white hover:bg-green-700 disabled:cursor-not-allowed disabled:opacity-60 dark:border-green-900/40"
                      >
                        <FileCheck className="w-3.5 h-3.5" />
                        {statusActionLoading === 'approved' ? 'Approving…' : 'Mark Approved'}
                      </button>
                      <button
                        onClick={() => void handleUpdateProposalStatus('rejected')}
                        disabled={statusActionLoading !== null}
                        className="inline-flex items-center gap-1.5 rounded-lg border border-red-200 px-3 py-2 text-xs font-medium text-red-700 hover:bg-red-100 disabled:cursor-not-allowed disabled:opacity-60 dark:border-red-900/40 dark:text-red-300 dark:hover:bg-red-950/40"
                      >
                        <X className="w-3.5 h-3.5" />
                        {statusActionLoading === 'rejected' ? 'Rejecting…' : 'Mark Rejected'}
                      </button>
                    </>
                  )}

                  {currentProposal.status === 'approved' && onConvertToDocument && enabledConversionTargets.includes('quote') && (
                    <>
                      <button
                        onClick={() => void handleConvertDocument('quote')}
                        className="inline-flex items-center gap-1.5 rounded-lg border border-green-200 bg-green-600 px-3 py-2 text-xs font-medium text-white hover:bg-green-700 dark:border-green-900/40"
                      >
                        <FileCheck className="w-3.5 h-3.5" />
                        Convert to Quote
                      </button>
                      {enabledConversionTargets.includes('invoice') && (
                        <button
                          onClick={() => void handleConvertDocument('invoice')}
                          className="inline-flex items-center gap-1.5 rounded-lg border border-green-200 px-3 py-2 text-xs font-medium text-green-700 hover:bg-green-100 dark:border-green-900/40 dark:text-green-300 dark:hover:bg-green-950/40"
                        >
                          <FileText className="w-3.5 h-3.5" />
                          Convert to Invoice
                        </button>
                      )}
                    </>
                  )}

                  {currentProposal.status === 'approved' && onConvertToDocument && !enabledConversionTargets.includes('quote') && enabledConversionTargets.includes('invoice') && (
                    <button
                      onClick={() => void handleConvertDocument('invoice')}
                      className="inline-flex items-center gap-1.5 rounded-lg border border-green-200 bg-green-600 px-3 py-2 text-xs font-medium text-white hover:bg-green-700 dark:border-green-900/40"
                    >
                      <FileText className="w-3.5 h-3.5" />
                      Convert to Invoice
                    </button>
                  )}

                  {currentProposal.status === 'converted' && onOpenConvertedDocument && currentProposal.converted_document_id && (
                    <button
                      onClick={() => void handleOpenConvertedDocument()}
                      className="inline-flex items-center gap-1.5 rounded-lg border border-purple-200 bg-purple-600 px-3 py-2 text-xs font-medium text-white hover:bg-purple-700 dark:border-purple-900/40"
                    >
                      <ExternalLink className="w-3.5 h-3.5" />
                      Open {currentProposal.converted_document_type === 'invoice' ? 'Invoice' : 'Quote'}
                    </button>
                  )}
                </div>
              </div>
            </div>
          )}

          <div className="mt-3 rounded-xl border border-gray-200 bg-white/70 px-3 py-3 dark:border-gray-700 dark:bg-gray-900/40">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div className="min-w-0">
                <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                  Proposal Versions
                </p>
                <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">
                  {sortedRevisions.length > 0
                    ? `${sortedRevisions.length} saved revision${sortedRevisions.length === 1 ? '' : 's'} · Current revision ${currentProposal.current_revision_number || sortedRevisions[0]?.revision_number || 0}`
                    : 'No saved revisions yet. Capture a version before major edits or client review.'}
                </p>
              </div>

              <div className="flex flex-wrap items-center gap-2">
                <button
                  onClick={() => void createRevision()}
                  disabled={isSavingRevision || loading || loadingProposal}
                  className="inline-flex items-center gap-1.5 rounded-lg border border-gray-300 px-3 py-2 text-xs font-medium text-gray-700 hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-60 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-800"
                >
                  <History className="w-3.5 h-3.5" />
                  {isSavingRevision ? 'Saving…' : 'Save Revision'}
                </button>
                <button
                  onClick={() => setShowRevisionHistory((prev) => !prev)}
                  className="inline-flex items-center gap-1.5 rounded-lg border border-blue-200 px-3 py-2 text-xs font-medium text-blue-700 hover:bg-blue-100 dark:border-blue-900/40 dark:text-blue-300 dark:hover:bg-blue-950/40"
                >
                  {showRevisionHistory ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                  {showRevisionHistory ? 'Hide History' : 'Show History'}
                </button>
              </div>
            </div>

            {showRevisionHistory && (
              <div className="mt-3 space-y-2">
                {sortedRevisions.length === 0 ? (
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    No revisions saved yet.
                  </p>
                ) : (
                  sortedRevisions.map((revision) => {
                    const isActiveRevision = currentProposal.active_revision_id === revision.id;
                    const isRestoreDisabled = currentProposal.status === 'converted' || revision.status === 'converted' || restoringRevisionId === revision.id;

                    return (
                      <div
                        key={revision.id}
                        className={`rounded-lg border px-3 py-3 ${isActiveRevision ? 'border-blue-300 bg-blue-50 dark:border-blue-900/50 dark:bg-blue-950/30' : 'border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-900/50'}`}
                      >
                        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                          <div className="min-w-0">
                            <div className="flex flex-wrap items-center gap-2">
                              <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                                {revision.label}
                              </p>
                              <span className={`px-2 py-0.5 text-[11px] font-medium rounded ${getProposalStatusColor(revision.status)}`}>
                                {getProposalStatusLabel(revision.status)}
                              </span>
                              {isActiveRevision && (
                                <span className="px-2 py-0.5 text-[11px] font-medium rounded bg-blue-600 text-white">
                                  Active
                                </span>
                              )}
                            </div>
                            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                              Rev {revision.revision_number} · {new Date(revision.created_at).toLocaleString()}
                            </p>
                            {revision.note && (
                              <p className="mt-2 text-sm text-gray-600 dark:text-gray-300">{revision.note}</p>
                            )}
                            <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                              {revision.currency} {revision.grand_total.toFixed(2)} · {revision.line_items.length} item{revision.line_items.length === 1 ? '' : 's'}
                            </p>
                          </div>

                          {!isActiveRevision && (
                            <button
                              onClick={() => void restoreRevision(revision.id)}
                              disabled={isRestoreDisabled}
                              className="inline-flex items-center gap-1.5 rounded-lg border border-gray-300 px-3 py-2 text-xs font-medium text-gray-700 hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-60 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-800"
                            >
                              {restoringRevisionId === revision.id ? 'Restoring…' : 'Restore'}
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            )}
          </div>

          <div className="mt-3 rounded-xl border border-gray-200 bg-white/70 px-3 py-3 dark:border-gray-700 dark:bg-gray-900/40">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
              <div className="min-w-0">
                <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                  Reference Documents
                </p>
                <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">
                  {referenceDocuments.length > 0
                    ? `${referenceDocuments.length} uploaded reference document${referenceDocuments.length === 1 ? '' : 's'} currently support this proposal.`
                    : referenceUploadsEnabled
                      ? 'Upload PDFs, DOCX files, images, and text documents to ground AI-generated scope, pricing, and assumptions.'
                      : 'Reference document uploads are disabled for this client in AI Proposal Manager settings.'}
                </p>
              </div>

              <div className="flex flex-wrap items-center gap-2 text-[11px] font-medium">
                <span className={`rounded-full px-2.5 py-1 ${referenceUploadsEnabled ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300' : 'bg-gray-200 text-gray-600 dark:bg-gray-800 dark:text-gray-300'}`}>
                  Uploads {referenceUploadsEnabled ? 'enabled' : 'disabled'}
                </span>
                <span className={`rounded-full px-2.5 py-1 ${referenceParsingEnabled ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300' : 'bg-amber-100 text-amber-700 dark:bg-amber-900/20 dark:text-amber-300'}`}>
                  AI parsing {referenceParsingEnabled ? 'enabled' : 'disabled'}
                </span>
                <span className={`rounded-full px-2.5 py-1 ${referenceExportsEnabled ? 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300' : 'bg-gray-200 text-gray-600 dark:bg-gray-800 dark:text-gray-300'}`}>
                  Exports {referenceExportsEnabled ? 'include docs' : 'exclude docs'}
                </span>
              </div>
            </div>

            {referenceDocuments.length > 0 ? (
              <div className="mt-3 grid gap-2">
                {referenceDocuments.map((document) => {
                  const Icon = document.attachmentType === 'image' || document.attachmentType === 'photo'
                    ? ImageIcon
                    : FileText;
                  const fileSizeLabel = formatAttachmentFileSize(document.sizeBytes || undefined);

                  return (
                    <div
                      key={`${document.id}-${document.fileName || document.title}`}
                      className="rounded-lg border border-gray-200 bg-white px-3 py-3 dark:border-gray-700 dark:bg-gray-950/40"
                    >
                      <div className="flex items-start gap-3">
                        <div className={`mt-0.5 flex h-9 w-9 items-center justify-center rounded-lg ${document.attachmentType === 'image' || document.attachmentType === 'photo' ? 'bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-300' : 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300'}`}>
                          <Icon className="h-4 w-4" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="flex flex-wrap items-center gap-2">
                            <p className="truncate text-sm font-medium text-gray-900 dark:text-gray-100">{document.title}</p>
                            {document.parsedForContext && (
                              <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-medium text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300">
                                parsed for AI context
                              </span>
                            )}
                          </div>
                          <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
                            {document.fileName && <span>{document.fileName}</span>}
                            {document.extractedFrom && <span>Source: {document.extractedFrom.toUpperCase()}</span>}
                            {fileSizeLabel && <span>{fileSizeLabel}</span>}
                          </div>
                          {document.warnings.length > 0 && (
                            <div className="mt-2 flex items-start gap-1.5 rounded-lg bg-amber-50 px-2 py-1.5 text-xs text-amber-700 dark:bg-amber-900/20 dark:text-amber-300">
                              <AlertTriangle className="mt-0.5 h-3.5 w-3.5 flex-shrink-0" />
                              <span>{document.warnings[0]}</span>
                            </div>
                          )}
                          {document.textPreview && (
                            <p className="mt-2 line-clamp-3 text-xs text-gray-600 dark:text-gray-300">{document.textPreview}</p>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="mt-3 rounded-lg border border-dashed border-gray-300 px-3 py-3 text-xs text-gray-500 dark:border-gray-700 dark:text-gray-400">
                {referenceUploadsEnabled
                  ? 'Reference documents attached in the chat composer will appear here and stay linked to the proposal for future revisions.'
                  : 'Enable reference document uploads in Industry Solution Settings to let teams attach contextual files here.'}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Line Item Editor Toggle */}
      {!compact && currentProposal && (
        <div className="px-4 py-2 border-b border-gray-200 dark:border-gray-700">
          <button
            onClick={() => setShowLineItemEditor(!showLineItemEditor)}
            className="text-sm text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300"
          >
            {showLineItemEditor ? 'Hide' : 'Show'} Line Items
          </button>
        </div>
      )}

      {!compact && showLineItemEditor && currentProposal && (
        <div className="px-4 py-2 border-b border-gray-200 dark:border-gray-700">
          <ProposalLineItemEditor
            lineItems={currentProposal.line_items}
            onChange={(items) => {
              setCurrentProposal((prev) => {
                if (!prev) {
                  return prev;
                }

                return {
                  ...prev,
                  line_items: items,
                };
              });
              queueProposalPatch({ line_items: items });
            }}
            currency={currentProposal.currency}
            isReadOnly={!isManualProposalEditingAllowed}
            enableItemSearch={true}
            itemSearchContactId={currentProposal.contact_id ?? undefined}
            itemSearchContactName={currentProposal.contact_name ?? undefined}
          />
        </div>
      )}

      {loadingProposal && (
        <div className="px-4 py-2 text-sm text-gray-600 dark:text-gray-400 border-b border-gray-200 dark:border-gray-700">
          Loading proposal…
        </div>
      )}

      {/* AI Chat Interface */}
      <div className="flex-1 overflow-hidden">
        <AIAssistantChat
          moduleContext="proposals"
          moduleKey="total-operations"
          title="AI Proposal Assistant"
          subtitle="Powered by Total Access"
          welcomeTitle={compact ? 'Refine this quote' : 'How can I help with quotes?'}
          welcomeMessage={compact
            ? 'Ask for scope changes, pricing adjustments, stronger client wording, or a cleaner commercial structure.'
            : 'I can help you create, review, and manage quotes. Ask me to generate a new quote, review an existing one, or suggest improvements.'}
          inputPlaceholder="Ask me to create or review a quote..."
          userName={user?.first_name || user?.email || undefined}

          messages={conversation}
          isGenerating={isGenerating || loading}
          creditsRemaining={credits?.remaining ?? 0}
          creditsTotal={credits?.total ?? 0}
          creditsResetDate={credits?.resetDate}
          showCredits={true}

          quickActions={compact ? [] : quickActions}
          examplePrompts={compact ? [] : examplePrompts}
          showQuickActions={false}
          showQuickActionsInHeader={false}
          showProviderToggle={showProviderToggle}

          onSendMessage={handleSendMessage}
          onApplyAction={handleExecuteAction}
          onClose={!embedded && !compact ? handleAssistantClose : undefined}
          onFeedback={feedback}
          renderAttachment={renderProposalAttachment}

          showHistory={false}

          inputRows={compact ? 2 : 3}
          inputMinHeightClassName={compact ? 'min-h-[72px]' : 'min-h-[88px]'}
          inputMaxHeightClassName={compact ? 'max-h-[176px]' : 'max-h-[240px]'}
          allowAttachments={referenceUploadsEnabled}
          headerStorageKey="ta_ai_proposal_assistant_header"
          closable={Boolean(onClose) && !embedded && !compact}
          hideHeader={embedded || compact}
          disableInitialScrollReset={true}
        />
      </div>
    </div>
  );
}

